<template>
  <Flow
    class="absolute top-0 left-0 w-full h-full"
    :nodes="nodes"
    :edges="[]"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useVueFlow, type Edge, type Node } from "@vue-flow/core";
import Elk from "elkjs/lib/elk.bundled.js";
import Flow from "./Flow.vue";
const { algorithm = "mrtree" } = defineProps<{
  algorithm?: "mrtree" | "layered";
}>();
const layoutOptions = computed(() => ({
  "elk.algorithm": algorithm,
  "elk.direction": "DOWN",
  "elk.spacing.nodeNode": "100",
  "elk.edgeRouting": "ORTHOGONAL",
  "elk.incremental": "true",
  "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
}));

const elk = new Elk();
const nodes = ref<Node[]>([]);
elk.knownLayoutAlgorithms().then((a) => console.log(a));
const { nodes: vueFlowNodes } = useVueFlow({ id: "layoutManager" });

async function generateLayout({
  edges,
  nodes: nodesList,
}: {
  nodes: Node[];
  edges: Edge[];
}): Promise<{
  id: string;
  children: {
    id: string;
    width: number;
    height: number;
    data: unknown;
    type: string;
    x?: number;
    y?: number;
  }[];
  edges: {
    id: string;
    sources: string[];
    targets: string[];
    sections: {
      id: string;
      startPoint: { x: number; y: number };
      endPoint: { x: number; y: number };
      bendPoints?: { x: number; y: number }[];
    }[];
  }[];
}> {
  nodes.value = nodesList;
  await new Promise((resolve) => setTimeout(resolve, 100));
  const graph: {
    id: string;
    children: {
      id: string;
      width: number;
      height: number;
      data: unknown;
      type: string;
      x?: number;
      y?: number;
    }[];
    edges: {
      id: string;
      sources: string[];
      targets: string[];
      sections: {
        id: string;
        startPoint: { x: number; y: number };
        endPoint: { x: number; y: number };
        bendPoints?: { x: number; y: number }[];
      }[];
    }[];
  } = {
    id: "root",

    children:
      vueFlowNodes.value.map((n) => ({
        id: n.id,
        width: (n?.dimensions.width as number) + 10,
        height: (n?.dimensions.height as number) ?? 40,
        data: n.data,
        type: n.type ?? "",
        x: n.position.x,
        y: n.position.y,
      })) ?? [],

    edges:
      edges.map((n) => ({
        id: n.id,
        sources: [n.source],
        targets: [n.target],
        sections: [],
      })) ?? [],
  };
  await elk.layout(graph, {
    layoutOptions: layoutOptions.value,
  });

  const root = graph.children.find((n) => n.id === "1");
  if (root) {
    const padding = 100;
    const dx = root.x ? -root.x : 0;
    const dy = root.y ? -root.y : 0;
    graph.children = graph.children.map((n) => {
      n.x = (n.x ?? 0) + dx + padding;
      n.y = (n.y ?? 0) + dy + padding;
      return n;
    });
    graph.edges = graph.edges.map((e) => {
      e.sections = e.sections.map((s) => {
        s.startPoint.x += dx + padding;
        s.startPoint.y += dy + padding;
        s.endPoint.x += dx + padding;
        s.endPoint.y += dy + padding;
        s.bendPoints = s.bendPoints?.map((p) => ({
          x: p.x + dx + padding,
          y: p.y + dy + padding,
        }));
        return s;
      });
      return e;
    });
  }

  return graph;
}

defineExpose({
  generateLayout,
});
</script>
